<template>
  <DropdownSelect
      :label="label"
      :modelValue="modelValue"
      :options="questionOptions"
      :maxTextLength="maxTextLength"
      :loading="loading"
      @update:modelValue="handleSelectionChange"
  />
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DropdownSelect from './DropdownSelect.vue';
import reportsService from '@/service/reportsService';

const { t } = useI18n();

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  modelValue: {
    type: [String, Number],
    default: 'all'
  },
  maxTextLength: {
    type: Number,
    default: 15
  },
  tabType: {
    type: String,
    default: 'test'
  }
});

const emit = defineEmits(['update:modelValue', 'optionsLoaded', 'filterChange']);

const questionOptions = ref([]);
const loading = ref(false);
const rawQuestionData = ref([]);

const processQuestionRanges = (data) => {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  const questionCounts = data
      .map(item => {
        return item.questionCount || item.questions?.length || item.totalQuestions || 0;
      })
      .filter(count => count > 0);

  if (questionCounts.length === 0) {
    return [{ value: 'all', label: t('allQuestions') }];
  }

  const minQuestions = Math.min(...questionCounts);
  const maxQuestions = Math.max(...questionCounts);

  const ranges = [];

  ranges.push({ value: 'all', label: t('allQuestions') });

  if (maxQuestions <= 50) {
    for (let i = 0; i < maxQuestions; i += 10) {
      const start = i === 0 ? 1 : i + 1;
      const end = Math.min(i + 10, maxQuestions);
      const value = `${start}-${end}`;
      const label = `${start}-${end} ${t('question')}`;
      ranges.push({ value, label });
    }
  } else if (maxQuestions <= 200) {
    for (let i = 0; i < maxQuestions; i += 25) {
      const start = i === 0 ? 1 : i + 1;
      const end = Math.min(i + 25, maxQuestions);
      const value = `${start}-${end}`;
      const label = `${start}-${end} ${t('question')}`;
      ranges.push({ value, label });
    }
  } else {
    for (let i = 0; i < maxQuestions; i += 50) {
      const start = i === 0 ? 1 : i + 1;
      const end = Math.min(i + 50, maxQuestions);
      const value = `${start}-${end}`;
      const label = `${start}-${end} ${t('question')}`;
      ranges.push({ value, label });
    }
  }

  if (maxQuestions > 200) {
    ranges.push({
      value: '200+',
      label: `200+ ${t('question')} (Max: ${maxQuestions})`
    });
  }

  return ranges;
};

const fetchQuestionData = async () => {
  loading.value = true;

  try {
    const apiTypeMapping = {
      test: 'selfAssessments',
      program: 'programs',
      gamelab: 'gameLabs',
      user: 'users'
    };

    const apiType = apiTypeMapping[props.tabType] || 'selfAssessments';

    const params = {
      type: apiType,
      parameterType: 'properties',
      status: 1,
      page: 1,
      limit: 100
    };

    const response = await reportsService.getReportData(params);

    if (response.data && response.data.data) {
      rawQuestionData.value = response.data.data;

      const ranges = processQuestionRanges(response.data.data);
      questionOptions.value = ranges;

      emit('optionsLoaded', ranges);

    } else {
      questionOptions.value = [
        { value: 'all', label: t('allQuestions') },
        { value: '1-25', label: `1-25 ${t('question')}` },
        { value: '26-50', label: `26-50 ${t('question')}` },
        { value: '51+', label: `51+ ${t('question')}` }
      ];
    }

  } catch (error) {
    questionOptions.value = [
      { value: 'all', label: t('allQuestions') },
      { value: '1-25', label: `1-25 ${t('question')}` },
      { value: '26-50', label: `26-50 ${t('question')}` },
      { value: '51+', label: `51+ ${t('question')}` }
    ];

    emit('optionsLoaded', questionOptions.value);

  } finally {
    loading.value = false;
  }
};

const handleSelectionChange = (selectedValue) => {
  let questionCountParams = null;

  if (selectedValue && selectedValue !== 'all') {
    if (selectedValue.includes('-')) {
      const [min, max] = selectedValue.split('-').map(num => {
        if (num.includes('+')) {
          return parseInt(num.replace('+', ''));
        }
        return parseInt(num);
      });

      questionCountParams = {
        min: min || 0,
        max: selectedValue.includes('+') ? 999 : (max || 999)
      };

    } else if (selectedValue.includes('+')) {
      const minValue = parseInt(selectedValue.replace('+', ''));
      questionCountParams = {
        min: minValue,
        max: 999
      };
    }
  }

  emit('update:modelValue', selectedValue);

  emit('filterChange', {
    questionCount: questionCountParams,
    selectedRange: selectedValue
  });
};

watch(() => props.tabType, async (newTabType) => {
  if (newTabType) {
    await fetchQuestionData();
  }
});

onMounted(async () => {
  await fetchQuestionData();
});

defineExpose({
  refreshData: fetchQuestionData,
  getQuestionData: () => rawQuestionData.value
});
</script>
